"use client";

import {
  getCurrentDate,
  toPlainDateString,
  type PlainDate,
} from "@sa-nu/shared-utils/date";
import { useState } from "react";

import { useGtm } from "../../../../../../lib/google-tag-manager/gtm";
import type { buildAccommodationReservationProviderInput } from "../../../../../../utils";
import { useReservationEstimate } from "../../../../_hooks/use-reservation-estimate";
import { DatePicker } from "../../../../dx/ui/date-picker";
import type { DatePickerSelection } from "../../../../dx/ui/date-picker";
import { LoadingIndicator } from "../../../../dx/ui/loading-indicator";
import { CombineDateInput } from "../../../_shared/combine-date-input";
import type { SelectedAccommodationReservation } from "../../reservation-calendar";
import { useAccommodationDayPrices } from "../_hooks/use-accommodation-day-prices";
import { useCheckoutAvailabilities } from "../_hooks/use-checkout-availabilities";
import { useStayTicketFeeDiscounts } from "../_hooks/use-stay-ticket-fee-discounts";
import type { CheckoutPickerPhase } from "../_utils/build-checkout-date-info-map";
import { buildCheckoutDateInfoMap } from "../_utils/build-checkout-date-info-map";
import { formatConsecutiveNightDiscountNotices } from "../_utils/format-consecutive-night-discount-notices";

import { CheckoutHeader } from "./checkout-header";
import { CheckoutSubmitButton } from "./checkout-submit-button";

export const CHECKOUT_DATE_PICKER_JA_MESSAGES = {
  title: "宿泊期間の選択",
  submitButton: "予約する",
  totalPrefix: "合計",
  loadingPrice: "...",
  loadingPriceOverlay: "料金を読み込み中...",
  errorBody: "データを取得できませんでした。時間をおいて再度お試しください。",
  closeLabel: "閉じる",
} as const;

export type CheckoutDatePickerMessages =
  typeof CHECKOUT_DATE_PICKER_JA_MESSAGES;

export type CheckoutDatePickerOnSelect = (
  selected: Omit<SelectedAccommodationReservation, "accommodationFacilities">,
) => void;

export type CheckoutDatePickerContentProps = {
  accommodationId: string;
  /** タップした日 = CI 候補 */
  firstNight: PlainDate;
  provider: ReturnType<typeof buildAccommodationReservationProviderInput>;
  /** 予約可能範囲の先頭夜 */
  minNight: PlainDate;
  /** 予約可能範囲の末尾夜 (泊まれる最終の夜) */
  maxNight: PlainDate;
  /** CO 確定時。close は呼び出し側 (closeAll 等) が行う */
  onSelect: CheckoutDatePickerOnSelect;
  /** close ボタン押下時。各シェルが open=false にする */
  onRequestClose: () => void;
  /** dialog の aria-labelledby が指す id。ヘッダーの可視タイトル h2 (CheckoutHeader) に付く */
  titleId: string;
  /** テスト/Storybook で決定的な today にするため任意注入 (デフォルト: getCurrentDate()) */
  today?: PlainDate;
  messages?: CheckoutDatePickerMessages;
};

/**
 * チェックアウト日選択のシェル非依存な中身 (header / DatePicker / notice / footer CTA)。
 * モバイル Drawer / PC Dialog の双方から使う。
 * a11y タイトルはヘッダーの可視 h2 (id=titleId, CheckoutHeader 内) が担い、
 * 各シェルが Popup に aria-labelledby={titleId} を付ける。
 */
export function CheckoutDatePickerContent({
  accommodationId,
  firstNight,
  provider,
  minNight,
  maxNight,
  onSelect,
  onRequestClose,
  titleId,
  today = getCurrentDate(),
  messages = CHECKOUT_DATE_PICKER_JA_MESSAGES,
}: CheckoutDatePickerContentProps) {
  // 初期状態: CI = firstNight (タップ日)、CO 未選択 → selectingEnd で開く
  const [selection, setSelection] = useState<DatePickerSelection>({
    startDate: firstNight,
    endDate: null,
  });

  const phase = derivePhase(selection);
  const ciNight = selection.startDate;
  // CO 日 (= DatePicker の endDate) から lastNight (= CO - 1) を導出
  const coDate = phase === "confirmed" ? selection.endDate : null;
  const lastNight = coDate !== null ? coDate.subtract({ days: 1 }) : null;

  // priceMap は minNight..maxNight の固定範囲で 1 回 fetch
  const {
    priceMap,
    unavailableDates,
    loading: priceLoading,
    error: priceError,
  } = useAccommodationDayPrices({
    accommodationId,
    firstNight: minNight,
    lastNight: maxNight,
    provider,
  });

  // CO window は CI 変更で再 fetch。
  const {
    maxNights,
    coDisabledDates,
    loading: coWindowLoading,
    error: coWindowError,
  } = useCheckoutAvailabilities({
    accommodationId,
    firstNight: ciNight,
    provider,
  });

  // 料金割引は CI 非依存のチケット属性なので、CI 必須の CO window とは別 query
  // (stayTicketReservationInfo, unitId 起点) で取得する。
  const { feeDiscounts } = useStayTicketFeeDiscounts({ provider });

  // 注意書き = 連泊割引のみを「N泊以上の場合、宿泊料金が◯◯OFF」に整形 (特定泊の値引きは対象外)。
  const notices = formatConsecutiveNightDiscountNotices(
    feeDiscounts.filter((d) => d.ruleType === "ConsecutiveNightDiscount"),
  );

  const dataError = priceError ?? coWindowError;

  // CO window fetch 中はすべての CO 候補を disabled にしてちらつき/不正タップを防ぐ。
  // maxNights=0 の場合、isValidEndDateCandidate() を満たす候補が存在しないため
  // 全日 disabled になる (DatePicker の動作確認済み)。
  const effectiveMaxNights = coWindowLoading ? 0 : maxNights;

  const { total, loading: estimateLoading } = useReservationEstimate({
    accommodationId,
    provider,
    firstNight: ciNight,
    lastNight,
  });

  const dateInfoMap = buildCheckoutDateInfoMap({
    phase,
    priceMap,
    unavailableDates,
    coDisabledDates,
    endDate: coDate !== null ? toPlainDateString(coDate) : undefined,
  });

  // maxDate: maxNight+1 まで拡張する。
  // 理由: maxNight は「最後に泊まれる夜」。チェックアウトは maxNight の翌日まで選べる必要があるため。
  // 例: maxNight = 3/31 → CO は 4/1 まで選択可。DatePicker の maxDate はその選択可能な末尾日。
  // 過剰な選択はバックエンドの maxNights / coDisabledDates が二重ガードするため問題なし。
  const maxDate = maxNight.add({ days: 1 });

  const gtm = useGtm();
  const isConfirmed = phase === "confirmed";

  const handleSubmit = () => {
    if (!isConfirmed || ciNight === null || lastNight === null) return;
    gtm.sendEvent("rsv_cal_checkout_selected");
    onSelect({
      accommodationId,
      firstNight: ciNight,
      lastNight,
    });
  };

  return (
    <>
      <CheckoutHeader
        titleId={titleId}
        onRequestClose={onRequestClose}
        messages={messages}
      />

      <CombineDateInput
        startDate={selection.startDate}
        endDate={selection.endDate}
      />

      {/* Body: Popup は高さ固定で flex item 内の percentage height (DatePicker root の h-full)
          が auto に落ち、content 高さまで伸びて notice/footer に重なるのを防ぐ。
          absolute inset-0 の箱で高さを確定させ DatePicker 自身をスクロールコンテナにする。
          ラッパー側に overflow-y-auto を移すと sticky ヘッダーが内側の
          スクロールしないポートに張り付くため不可 */}
      {dataError ? (
        <ContentBodyError message={messages.errorBody} />
      ) : (
        <div className="relative min-h-0 flex-1">
          <div className="absolute inset-0">
            <DatePicker
              minDate={minNight}
              maxDate={maxDate}
              startDate={selection.startDate}
              endDate={selection.endDate}
              onChange={setSelection}
              maxNights={effectiveMaxNights}
              dateInfoMap={dateInfoMap}
              today={today}
            />
          </div>
          {priceLoading && (
            <PriceLoadingOverlay label={messages.loadingPriceOverlay} />
          )}
        </div>
      )}

      {/* 注意書き (任意・複数行)。割引案内等。Figma 5001-37878 準拠 (赤字・中央) */}
      {notices.length > 0 && (
        <div className="flex shrink-0 flex-col gap-[10px] px-[16px] pt-[12px]">
          {notices.map((line) => (
            <p
              key={line}
              className="font-notojp text-dx-button-primary-fill w-full text-center text-[12px] leading-[18px] font-normal [word-break:break-word]"
            >
              {line}
            </p>
          ))}
        </div>
      )}

      <CheckoutSubmitButton
        isConfirmed={isConfirmed}
        total={total}
        estimateLoading={estimateLoading}
        onSubmit={handleSubmit}
        messages={messages}
      />
    </>
  );
}

function derivePhase(selection: DatePickerSelection): CheckoutPickerPhase {
  if (selection.startDate === null) return "idle";
  if (selection.endDate === null) return "selectingEnd";
  return "confirmed";
}

function PriceLoadingOverlay({ label }: { label: string }) {
  return (
    <div className="bg-dx-surface-base/75 absolute inset-0 z-10 flex items-center justify-center">
      <LoadingIndicator label={label} />
    </div>
  );
}

function ContentBodyError({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center px-[16px] py-[32px]">
      <p className="font-notojp text-dx-text-sub text-[14px] leading-[1.6]">
        {message}
      </p>
    </div>
  );
}
